/**
 * The closed enum catalogue (`docs/design/00-contracts.md` §5).
 *
 * Every entry here is a native PostgreSQL enum created in migration `0000`. Anything an
 * organization configures — departments, tags, task groups, stages, notification types,
 * `event_log.event_type`, `audit_log.entity_type` — is deliberately **not** here: principle 9
 * makes those rows, not code.
 *
 * Adding a value later is `ALTER TYPE ... ADD VALUE`; removing one is impossible, so no
 * speculative values. Display labels live under `enums.<type>.<value>` in `nb.json`/`en.json`;
 * the database never stores a label.
 */
import { z } from 'zod';

/**
 * The four member roles. `external` is gone (sak 232, 0465): an external is a personal account that
 * opens what is shared with them as a `guest` in the sharing organisation. The value stays in the
 * Postgres enum `org_role` (enum values cannot be dropped); a CHECK keeps anything from writing it.
 */
export const ORG_ROLES = ['superadmin', 'admin', 'member', 'limited'] as const;
export type OrgRole = (typeof ORG_ROLES)[number];
export const orgRoleSchema = z.enum(ORG_ROLES).meta({ id: 'OrgRole' });

/**
 * WHO CAN BE ASKING (sak 232, PLAN 1.1): the four member roles and `guest` — a person with a live
 * `external` share in an organisation they are not a member of.
 * `guest` is never stored: not in Postgres, not in `role_permissions`, not on an invitation. The
 * guest's rights are the fixed whitelist in `policy/guest.ts`, never a matrix column.
 */
export const ACTOR_ROLES = [...ORG_ROLES, 'guest'] as const;
export type ActorRole = (typeof ACTOR_ROLES)[number];
export const actorRoleSchema = z.enum(ACTOR_ROLES).meta({ id: 'ActorRole' });

export const EDITION_ROLES = ['owner', 'manager', 'member', 'viewer'] as const;
export type EditionRole = (typeof EDITION_ROLES)[number];
export const editionRoleSchema = z.enum(EDITION_ROLES).meta({ id: 'EditionRole' });

/**
 * SE OG ENDRE, INGENTING I MELLOM (bestillingen §5.2, 27.10.2026). «Kan kommentere» er borte for alle
 * eksterne: den som ser et delt objekt med chat, kan skrive i chatten (`GUEST_ACTIONS`). Enumen i basen
 * har fortsatt verdien `comment`, men ingen rad kan bære den (`ck_object_grants__no_comment`, 1100).
 */
export const GRANT_PERMISSIONS = ['view', 'edit'] as const;
export type GrantPermission = (typeof GRANT_PERMISSIONS)[number];
export const grantPermissionSchema = z.enum(GRANT_PERMISSIONS).meta({ id: 'GrantPermission' });

export const OBJECT_TYPES = [
  'organization',
  'festival',
  'edition',
  'person',
  'contact',
  'task',
  'thread',
  'message',
  'department',
  'document',
  'performance',
  /**
   * Artisten i bookingregisteret (migrering 0079) — det samtalen med byrået henger på.
   *
   * DEN MÅ STÅ HER, og ikke bare i basen. Innboksen viser hva en tråd handler om, og `ThreadLink`
   * bruker denne enumen: en tråd med en type kontrakten ikke kjenner blir en 500 på HELE innboksen,
   * ikke bare på den ene raden. Målt 8. september — innboksen svarte «Det oppsto en feil på
   * serveren» i det den første bookingsamtalen var skrevet.
   */
  'booking_artist',
  /**
   * SAMTALEN OM ÉN KVELD (0099) — festivalens budrad og byråets forespørsel.
   *
   * -------------------------------------------------------------------------------------------
   * DE STO I BASEN OG IKKE HER, OG DET FELTE HELE INNBOKSEN.
   *
   * Advarselen rett over beskriver feilen ordrett — «en tråd med en type kontrakten ikke kjenner
   * blir en 500 på HELE innboksen, ikke bare på den ene raden» — og den slo til igjen 10.
   * september, av nøyaktig samme grunn: 0099 la `booking_bid` og `booking_enquiry` i
   * `object_type`, og lista her ble stående på elleve.
   *
   * At den ble funnet av en e2e-sveip og ikke av Atle er flaks. To enumer som skal være like, og
   * som ingenting holder like, er en felle som kan fyre av når som helst.
   */
  'booking_bid',
  'booking_enquiry',
  /**
   * KALENDERHENDELSEN (0227) — og dette er TREDJE gang den samme fella fyrer.
   *
   * Advarselen står to ganger over denne linja, og den holdt igjen: migreringen la `event` i
   * `object_type` fordi et varsel må kunne peke på møtet det handler om, og lista her ble stående
   * på fjorten. Forskjellen denne gangen er at `enums-match-the-contract.spec.ts` tok den — vakta
   * som ble skrevet etter forrige gang, og som er grunnen til at ingen innboks rakk å svare 500.
   */
  'event',
  /* 0356: varselet om en vakt peker på vakta (201.14). */
  'shift',
  /* 0417: varselet om regionbeskyttelse peker på konserten. */
  'concert',
  /* 0457 (sak 232): en ressurs kan deles, og delingstråden peker på delingen (P4). */
  'resource',
  'object_grant',
  /* 0481 (sak 238): samtalen fra Control Room — tråden peker på kundens organisasjon. */
  'platform_support',
  /* 0640 (F14): kjøreplanen for én scene deles som lenke. */
  'stage',
  /* 0660 (F12, F13): en mappe og årgangens kart kan deles. Et dokument var her fra før. */
  'folder',
  'edition_map',
  /* 0622 (F18): spillestedet deler salens spesifikasjon og arrangementet. */
  'venue_room',
  'venue_night',
  /* 0936 (D4, QA13 #100): artisten deles med en ekstern manager. */
  'artist',
  /* 0970 (259.11): samtalen om en kontaktforespørsel, som følger personen. */
  'contact_request',
  /* 1035: varselet om en kontrakt peker på kontrakten (skuffen åpnes på siden til huset som leser). */
  'contract',
] as const;
export type ObjectType = (typeof OBJECT_TYPES)[number];
export const objectTypeSchema = z.enum(OBJECT_TYPES).meta({ id: 'ObjectType' });

/**
 * HVA SLAGS HUS EN ORGANISASJON ER (migrering 0081).
 *
 * Vedtak 93, 8. september: et bookingbyrå er en EGEN organisasjon, ikke en gjest hos festivalen.
 * Verdien avgjør hvilke sider som finnes — en festival har Festivaler og Booking, et byrå har
 * Artister — og INGENTING annet. Tilgang bestemmes fortsatt av rollene og `can()`; to systemer som
 * bestemmer det samme ville før eller siden vært uenige om det.
 *
 * `venue` og `supplier` kommer trolig senere. Derfor er dette en `text` med skranke i basen og ikke
 * en enum: 0079 målte hva en enumverdi koster å legge til under drizzles ene transaksjon.
 */
/**
 * `personal` er huset en som bare er artist får, der hen er eneste medlem (0377, sak 216). Det er
 * ikke en kunde, og menyen viser bare Konserter, Innboks, Kalender og Oppgaver der.
 */
/**
 * `platform` er Tvitvis egen organisasjon (0477, Control Room). Plattformadminene er medlemmer der og
 * får den vanlige menyen. Den er ingen kunde og telles aldri som en.
 */
export const ORGANIZATION_KINDS = ['festival', 'agency', 'personal', 'platform'] as const;
export type OrganizationKind = (typeof ORGANIZATION_KINDS)[number];
export const organizationKindSchema = z.enum(ORGANIZATION_KINDS).meta({ id: 'OrganizationKind' });

export const ORGANIZATION_STATUSES = ['trial', 'active', 'suspended'] as const;
export type OrganizationStatus = (typeof ORGANIZATION_STATUSES)[number];
export const organizationStatusSchema = z
  .enum(ORGANIZATION_STATUSES)
  .meta({ id: 'OrganizationStatus' });

export const USER_STATUSES = ['invited', 'active', 'disabled'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];
export const userStatusSchema = z.enum(USER_STATUSES).meta({ id: 'UserStatus' });

export const FESTIVAL_KINDS = ['festival', 'venue'] as const;
export type FestivalKind = (typeof FESTIVAL_KINDS)[number];
export const festivalKindSchema = z.enum(FESTIVAL_KINDS).meta({ id: 'FestivalKind' });

export const EDITION_STATUSES = ['planning', 'active', 'completed', 'archived'] as const;
export type EditionStatus = (typeof EDITION_STATUSES)[number];
export const editionStatusSchema = z.enum(EDITION_STATUSES).meta({ id: 'EditionStatus' });

export const TASK_STATUSES = ['todo', 'in_progress', 'blocked', 'done', 'cancelled'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
export const taskStatusSchema = z.enum(TASK_STATUSES).meta({ id: 'TaskStatus' });

/**
 * The statuses a person may CHOOSE. `cancelled` is deliberately not among them (decision 0013).
 *
 * Atle, 29 August, on what to do with it: "det siste. det gir oss også mulighet til å legge det
 * til enkelt igjen i fremtiden." So it stays in the enum, in the column and in every schema — a
 * task that is already cancelled still reads back, still renders its badge and still filters —
 * and it is simply never offered. Removing it from the database instead would have meant a
 * migration to take it out and another to put it back, plus deciding what happens to rows that
 * hold it.
 *
 * Every picker maps over THIS list. `TASK_STATUSES` remains the parse surface, because a value
 * the database can hold is a value the API must accept: narrowing the parser would turn an
 * existing row into a 500 the first time somebody opened it.
 *
 * Derived rather than written out, so adding a status to `TASK_STATUSES` reaches the pickers
 * without anybody remembering this file exists.
 */
export const HIDDEN_TASK_STATUSES: readonly TaskStatus[] = ['cancelled'];
export const TASK_STATUS_CHOICES = TASK_STATUSES.filter(
  (status) => !HIDDEN_TASK_STATUSES.includes(status),
);

/**
 * What a virus scanner has said about an uploaded file (sak 54).
 *
 * `skipped` IS NOT `clean`. Atle asked for scanning; nothing scans locally, and calling an
 * unchecked file clean would put a claim in the column that the production deployment then
 * inherits. `skipped` records that nobody looked — a fact about the deployment — so an operator
 * can find every file that arrived before a scanner existed, and a production environment can be
 * configured to refuse them.
 */
export const SCAN_STATUSES = ['pending', 'clean', 'infected', 'skipped'] as const;
export type ScanStatus = (typeof SCAN_STATUSES)[number];
export const scanStatusSchema = z.enum(SCAN_STATUSES).meta({ id: 'ScanStatus' });

/**
 * What an attachment hangs on. Exactly what the product can attach to today.
 *
 * A value here is a branch in `AttachmentsService`'s access switch, so a type nobody has written a
 * rule for cannot reach the table — which is why this is not `RESOURCE_TYPES`. Riders and press
 * material on the performance page are the next value, when that page is built.
 */
/**
 * HVEM ET MENNESKE PÅ EN PROGRAMPOST ER (PERFORMANCE.md fane 2, migrering 0172).
 *
 * ------------------------------------------------------------------------------------------------
 * `travelling`  bandet og crewet som REISER. Tallet reise, hotell og mat regnes av.
 * `management`  agenten og tour manager — byråets folk, som ikke nødvendigvis kommer.
 * `organiser`   FESTIVALENS EGNE: artistverten, produksjonslederen, den som møter bussen.
 *
 * Den tredje kom 12. september, av Atles egne ord i QA runde 5: «Festivalens egne folk må legges i
 * artistens grupper ... så artistverten havner i byråets reisefølge. Det trengs en egen gruppe for
 * festivalens stab/vertskap.»
 *
 * DEN ER MER ENN EN ETIKETT. Reisefølget er tallet logistikken regner av, så en artistvert i den
 * gruppa blir en seng og et måltid produksjonen ikke skal betale for — og hos byrået står de som
 * en av bandets folk.
 */
export const PERFORMANCE_PARTIES = ['travelling', 'management', 'organiser'] as const;
export type PerformanceParty = (typeof PERFORMANCE_PARTIES)[number];
export const performancePartySchema = z.enum(PERFORMANCE_PARTIES).meta({ id: 'PerformanceParty' });

export const TRAVEL_KINDS = ['arrival', 'departure', 'local'] as const;
export type TravelKind = (typeof TRAVEL_KINDS)[number];
export const travelKindSchema = z.enum(TRAVEL_KINDS).meta({ id: 'TravelKind' });

export const TRAVEL_MODES = ['flight', 'train', 'bus', 'car', 'boat', 'other'] as const;
export type TravelMode = (typeof TRAVEL_MODES)[number];
export const travelModeSchema = z.enum(TRAVEL_MODES).meta({ id: 'TravelMode' });

export const COST_LINE_KINDS = ['budget', 'actual'] as const;
export type CostLineKind = (typeof COST_LINE_KINDS)[number];
export const costLineKindSchema = z.enum(COST_LINE_KINDS).meta({ id: 'CostLineKind' });

/**
 * What an attachment hangs on. `advance_section` is the fourth, added on Atle's answer to sak
 * 61: «et dokument kan legges på flere steder, organisasjonen, arrangmentet, performance (rider
 * etc)». The list is meant to grow — see the pg enum of the same name for what each new value
 * costs, which is a branch in `assertOwnerVisible` and an access rule.
 */
export const ATTACHMENT_OWNERS = [
  'task',
  'message',
  'folder',
  'advance_section',
  'contract',
  /**
   * En artist i bookingregisteret (migrering 0105).
   *
   * Artistsiden har hatt LENKER siden vedtak 96.1 — en teknisk rider som en URL, pressebilder i en
   * mappe et sted ute. Det holder når materialet ligger der, og holder ikke når det ikke gjør det:
   * en signert avtale, en PDF-rider, et scenekart. Filene hører til artisten og ikke til én
   * booking, fordi rideren er den samme uansett hvem som booker dem.
   */
  'booking_artist',
  /**
   * ÅRGANGEN SELV (migrering 0223, KOI Del 2).
   *
   * Områdeplanen — PNG-en eller JPG-en som legges over kartet mellom to hjørner — hører til året
   * og ikke til en mappe noen kan slette. Den er den eneste filen dette eierskapet har i dag, og
   * den er nok: et kart som mister bildet sitt fordi dokumenthylla ble ryddet er et kart ingen
   * stoler på.
   */
  'edition',
  /** Bilder og dokumenter på en sal (0408). Cellene er salens: `venue:read` og `venue:update`. */
  'venue_room',
  /**
   * INFO OG VEDLEGG PÅ RESSURSEN, POSTEN OG VAKTA (0925, QA13 #51). Arves nedover i lesingen:
   * den som står på vakta, leser vaktas, postens og ressursens filer gjennom `/v1/shifts/:id/info`.
   * Skriving krever `resource:update` eller `shift:update`.
   */
  'resource',
  'staffing_role',
  'shift',
  /**
   * DEN FELLES KONTRAKTEN (1017, sak 274 C4.3/C4.4): en egen PDF til signering, eller en kopi
   * signert utenfor Tvitvi. `owner_id` er kontrakten; begge parter kan laste opp. Fila ligger i
   * opplasterens organisasjon, og versjonen bærer lagringsnøkkelen og hashen, så motparten leser den
   * gjennom kontrakten.
   */
  'contract_document',
] as const;
export type AttachmentOwner = (typeof ATTACHMENT_OWNERS)[number];
export const attachmentOwnerSchema = z.enum(ATTACHMENT_OWNERS).meta({ id: 'AttachmentOwner' });

export const TASK_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];
export const taskPrioritySchema = z.enum(TASK_PRIORITIES).meta({ id: 'TaskPriority' });

export const MESSAGE_SOURCES = ['internal', 'email'] as const;
export type MessageSource = (typeof MESSAGE_SOURCES)[number];
export const messageSourceSchema = z.enum(MESSAGE_SOURCES).meta({ id: 'MessageSource' });

/** `chorus-md` bærer `@[[…]]`/`#[[…]]` og må gjennom vår renderer. Se 0229. */
/** `system` (0457, sak 232): delingstrådens systemlinjer, en JSON-kropp med i18n-nøkkel (P4). */
export const MESSAGE_FORMATS = ['plain', 'markdown', 'html', 'chorus-md', 'system'] as const;
export type MessageFormat = (typeof MESSAGE_FORMATS)[number];
export const messageFormatSchema = z.enum(MESSAGE_FORMATS).meta({ id: 'MessageFormat' });

export const AUDIT_ACTIONS = [
  'create',
  'update',
  'delete',
  'restore',
  'login',
  'logout',
  'invite',
  'accept_invite',
  'grant',
  'revoke',
  'role_change',
  'export',
  'merge',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
export const auditActionSchema = z.enum(AUDIT_ACTIONS).meta({ id: 'AuditAction' });

export const ACTOR_TYPES = ['user', 'system', 'service', 'anonymous'] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];
export const actorTypeSchema = z.enum(ACTOR_TYPES).meta({ id: 'ActorType' });

export const TERMS_KINDS = ['user_terms', 'privacy_policy', 'customer_terms', 'dpa'] as const;
export type TermsKind = (typeof TERMS_KINDS)[number];
export const termsKindSchema = z.enum(TERMS_KINDS).meta({ id: 'TermsKind' });

export const CONSENT_ACTIONS = ['accepted', 'withdrawn'] as const;
export type ConsentAction = (typeof CONSENT_ACTIONS)[number];
export const consentActionSchema = z.enum(CONSENT_ACTIONS).meta({ id: 'ConsentAction' });

export const EMAIL_SOURCES = ['manual', 'invitation', 'oauth', 'import'] as const;
export type EmailSource = (typeof EMAIL_SOURCES)[number];
export const emailSourceSchema = z.enum(EMAIL_SOURCES).meta({ id: 'EmailSource' });

/** The booking chain (decision 0011). Ordered for display; every transition is legal. */
export const PERFORMANCE_STATUSES = [
  'wishlist',
  'enquiry_sent',
  'hold',
  'verbally_confirmed',
  'deal_memo',
  'contract_sent',
  'signed',
  'announced',
  'performed',
  'cancelled',
] as const;
export const performanceStatusSchema = z
  .enum(PERFORMANCE_STATUSES)
  .meta({ id: 'PerformanceStatus' });
export type PerformanceStatus = (typeof PERFORMANCE_STATUSES)[number];

/**
 * Hva et navn på gjestelisten står der SOM.
 *
 * ---------------------------------------------------------------------------------------------
 * DEN VAR EN ENUM, OG DET VAR PLATTFORMEN SOM VALGTE ORDENE.
 *
 * The old note here argued that a free string cannot be counted — «a door list that says "artist"
 * for one entry and "band" for the next» — and that is true and was the wrong conclusion. Atle,
 * 2. september: «Alle valgene til type ser ut til å være hardkodet. Hvorfor, denne bør vel kunne
 * være redigerbar. Helst med at man kan lage nye når man legger til folk. De som allerede er brukt
 * lagres i dropdownmenyen.»
 *
 * Six words chosen by us means every festival whose vocabulary differs puts the difference under
 * «Annet» — and a column where a third of the rows say «other» is exactly the uncountable list the
 * enum was defending against. Principle 9: nothing organisation-specific is hardcoded.
 *
 * So the value is free text, and countability comes from the SUGGESTIONS instead: the field offers
 * what this organisation has already used, so the second person to type «Plateselskap» picks it
 * rather than inventing «plateselskap». That is a better guarantee than an enum, because it is the
 * one that survives the organisation needing a seventh word.
 */
export const GUEST_KIND_MAX = 40;
export const guestKindSchema = z
  .string()
  .trim()
  .min(1)
  .max(GUEST_KIND_MAX)
  .meta({ id: 'GuestKind' });
export type GuestKind = z.infer<typeof guestKindSchema>;

/**
 * The six the product ships with, and nothing more than a starting vocabulary.
 *
 * They are what a new organisation sees in the dropdown before it has written anything of its own,
 * and they are still the values every existing row holds — 0056 turned the column to text and left
 * the data alone. `other` is the column's default, so a name added without a thought lands
 * somewhere honest.
 */
export const GUEST_KIND_SUGGESTIONS = [
  'artist',
  'crew',
  'staff',
  'sponsor',
  'press',
  'other',
] as const;

/**
 * Hvor langt man har kommet MED ARTISTEN i bookingregisteret.
 *
 * EN ANNEN LISTE ENN `PERFORMANCE_STATUSES`, OG DET ER MED VILJE. En programpost er en avtale som
 * finnes; en artist i registeret er en vurdering som kanskje aldri blir til noe. Derfor har denne
 * lista `not_assessed` — «vi har ikke sett på dem ennå» — og `declined`, som er byråets nei, og
 * derfor mangler den `signed`, `announced` og `performed`: i det øyeblikket blir raden en ekte
 * programpost, og da er det bookingkjeden som gjelder.
 *
 * `dropped` er vårt eget nei, `declined` er deres. Å slå dem sammen ville gjort registeret
 * ubrukelig neste år, når spørsmålet er «hvem sa nei til OSS».
 */
export const BOOKING_ARTIST_STATUSES = [
  'not_assessed',
  'interested',
  'contacted',
  'offer_sent',
  'confirmed',
  'declined',
  'dropped',
] as const;
export const bookingArtistStatusSchema = z
  .enum(BOOKING_ARTIST_STATUSES)
  .meta({ id: 'BookingArtistStatus' });
export type BookingArtistStatus = (typeof BOOKING_ARTIST_STATUSES)[number];

/**
 * Interesse eller bud.
 *
 * To samtaler, ikke to nivåer av den samme: «er de ledige den helga» har ingen sum, «vi tilbyr
 * 120 000» har det. Skranken `ck_booking_bids__amount` i 0064 håndhever nettopp det, og en klient
 * som viser et beløpsfelt for en interesse har lest denne lista feil.
 */
export const BOOKING_BID_KINDS = ['interest', 'bid', 'offer'] as const;
export const bookingBidKindSchema = z.enum(BOOKING_BID_KINDS).meta({ id: 'BookingBidKind' });
export type BookingBidKind = (typeof BOOKING_BID_KINDS)[number];

/**
 * Hvor et bud står.
 *
 * `draft` er den eneste tilstanden uten `sentAt`, og skranken i basen sier det samme. Resten er
 * byråets svar, som fram til byråportalen finnes føres inn for hånd av den som fikk telefonen.
 * `countered` er ikke et avslag: byrået kom med et annet tall, og neste bud er en ny rad.
 */
export const BOOKING_BID_STATUSES = [
  'draft',
  'sent',
  'accepted',
  'declined',
  'countered',
  'withdrawn',
] as const;
export const bookingBidStatusSchema = z.enum(BOOKING_BID_STATUSES).meta({ id: 'BookingBidStatus' });
export type BookingBidStatus = (typeof BOOKING_BID_STATUSES)[number];

/**
 * Hvem som står på scenen, som fire ord og ikke som fritekst.
 *
 * DETTE ER ET FILTER, og det er hele grunnen til at det er en lukket liste: «Kun kvinner» er en
 * programmeringsbeslutning en festival tar bevisst, og et filter over fritekst slutter å virke den
 * dagen noen skriver «Kvinne» med stor K.
 *
 * `mixed` er et band med begge deler, `unknown` er en artist ingen har sett på ennå — og den står i
 * lista framfor å være null, slik at «hvor mange vet vi ikke om» kan telles.
 */
export const BOOKING_GENDERS = ['male', 'female', 'mixed', 'unknown'] as const;
export const bookingGenderSchema = z.enum(BOOKING_GENDERS).meta({ id: 'BookingGender' });
export type BookingGender = (typeof BOOKING_GENDERS)[number];
